/**
 * SAP CONFIDENTIAL
 *
 * This software is confidential proprietary software licensed by SAP SE
 * and is not to be used, duplicated or disclosed to anyone without the
 * prior written permission of SAP.
 *
 * (C) 2020 Copyright SAP SE.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';

// Modules
import { SharedModule } from './shared/shared.module';

// main component
import { AppComponent } from './app.component';

// theme specific
import { HeaderComponent } from './shared/header/header.component';
import { MainMenuComponent } from './shared/mainMenu/main-menu.component';
import { FooterComponent } from './shared/footer/footer.component';
import { FooterService } from './shared/services/footer.service';
import { CommonService } from './shared/services/common.service';

// Home
import { HomeComponent } from './components/home/home.component';
import { HomeService } from './components/home/home.service';

// Demo
import { DemoComponent } from './components/demo/demo.component';

// Service
import { CreatePlanService } from './shared/services/createplan.service';
import { PlanPeriodsService } from './shared/services/planperiods.service';
import { TitleService } from './shared/services/title.service';
import { PlanWizardService } from './shared/services/planwizard.service';
import { CalendarService } from './shared/services/calender.service';
import { PlanService } from './shared/services/plan.service';

//Application Setup Service PlanSetupWizardService
import { MarketService } from './shared/services/market.service';

import { LoaderComponent } from './components/loader/loader.component';
import { LoginService } from './components/login/login.service';
import { LoaderService } from './components/loader/loader.service';
import { ChangeSponsorService } from './shared/changesponsor/change-sponsor.service';
import { ReportFilterModalComponent } from './shared/filter-modal/filter-modal.component';
import { ColumnSelectorModalComponent } from './shared/column-modal/column-modal.component';
import { SaveNdShareReportModalComponent } from './shared/save-modal/save-modal.component';
import { DeleteReportModalComponent } from './shared/delete-report-modal/delete-report-modal.component';

/* Pre-book Assessments content goes here */
import { LoginComponent } from './components/login/login.component';
import { RegistrationComponent } from './components/login/registration/registration.component';
import { LogoutComponent } from './components/login/logout/logout.component';
import { SideMenuComponent } from './shared/side-menu/side-menu.component';
import { AuditLogsSerivce } from './shared/services/audit-logs.service';
import { AuditLogReportService } from './components/fieldsupport/audit-log-report/audit-log-report.service';
import { IntegrationAuditSerivce } from './shared/services/integrationAudit.service';
import { InboundLogReportService } from './shared/services/inbound-log-report.service';
import { GdprConfigurationService } from './components/administration/gdpr-configuration/gdpr-configuration.service';
import { GdprSchedulerService } from './components/administration/gdpr-scheduler/gdpr-scheduler.service';
import { CompensationAnalyticsService } from './shared/services/analytics.service';
import { BookmarkSliderService } from './shared/services/slider.service';
import { TieredCompensationsService } from './shared/services/tieredCompensations.service';
import { GenealogyService } from './shared/services/genealogy.service';
import { DistributorOrdersService } from './components/fieldsupport/distributorOrders/distributorOrders.service';
import { PaymentsService } from './shared/services/payments.service';
import { UtilitiesPanelService } from './shared/services/utilitiesPanel.service';
import { UserProfileComponent } from './components/userProfile/userProfile.component';
import { CustomTranslateLoader } from './shared/custom-translate-loader.component';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AgGridLicenseResolver } from './ag-grid-license.resolver';
import { ErrorComponent } from './components/error/error.component';
import { provideToastr, ToastrModule } from 'ngx-toastr';
import { ActivityLogReportService } from './components/fieldsupport/activity-log-report/activity-log-report.service';
import { InterfaceMonitoringReportService } from './components/fieldsupport/interface-monitoring-report/interface-monitoring-report.service';


const appRoutes: Routes = [
  { path: 'member', loadChildren: () => import('./components/demo/member/member.module').then((m) => m.MemberModule) },
  { path: 'order', loadChildren: () => import('./components/demo/order/order.module').then((m) => m.OrderModule) },
  {
    path: 'administration',
    loadChildren: () => import('./components/administration/administration.module').then((m) => m.AdministrationModule),
    resolve: { licenseKey: AgGridLicenseResolver }
  },
  {
    path: 'application',
    loadChildren: () => import('./components/application/application.module').then((m) => m.ApplicationModule),
    resolve: { licenseKey: AgGridLicenseResolver }
  },
  {
    path: 'fieldsupport',
    loadChildren: () => import('./components/fieldsupport/fieldsupport.module').then((m) => m.FieldSupportModule),
    resolve: { licenseKey: AgGridLicenseResolver }
  },
  {
    path: 'genealogyHome',
    loadChildren: () => import('./components/genealogy/genealogy.module').then((m) => m.GenealogyModule),
    resolve: { licenseKey: AgGridLicenseResolver }
  },
  {
    path: 'compensationHome',
    loadChildren: () => import('./components/compensation/compensation.module').then((m) => m.CompensationModule),
    resolve: { licenseKey: AgGridLicenseResolver }
  },
  { path: 'demo', component: DemoComponent },
  { path: 'home', component: HomeComponent },
  { path: 'login', component: LoginComponent },
  { path: 'resetPassword', component: LoginComponent },
  { path: 'profile/:id', component: UserProfileComponent },
  { path: 'registration/:id', component: RegistrationComponent },
  { path: 'forgotPassword/:id', component: RegistrationComponent },
  { path: 'logout', component: LogoutComponent },
  { path: 'errorPage', component: ErrorComponent },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: '**', redirectTo: '/home', pathMatch: 'full' }
  // { path: 'prebook', component: PreBookDataComponent },
  // { path: 'country', loadChildren: 'app/components/demo/country/country.module#CountryModule' },
  // { path: 'volumes', loadChildren: './components/demo/volumes/volumes.module#VolumesModule' },
  // { path: 'distributor-update', component: DistributorUpdateComponent},
  // { path: 'commissionrun-by-clientId', component: CommissionRunByClientComponent},
  // { path: 'prebookAssessments', component: PrebookAssessmentsComponent },
];

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    MainMenuComponent,
    DemoComponent,
    HomeComponent,
    LoaderComponent,
    ReportFilterModalComponent,
    ColumnSelectorModalComponent,
    SaveNdShareReportModalComponent,
    LoginComponent,
    RegistrationComponent,
    LogoutComponent,
    SideMenuComponent,
    DeleteReportModalComponent,
    UserProfileComponent,
    ErrorComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient]
      }
    }),
    RouterModule.forRoot(appRoutes, { useHash: true }),
    SharedModule
  ],
  providers: [
    CreatePlanService,
    PlanPeriodsService,
    TitleService,
    PlanWizardService,
    MarketService,
    FooterService,
    CommonService,
    LoginService,
    LoaderService,
    CalendarService,
    ChangeSponsorService,
    CompensationAnalyticsService,
    BookmarkSliderService,
    AuditLogsSerivce,
    AuditLogReportService,
    IntegrationAuditSerivce,
    InboundLogReportService,
    GdprConfigurationService,
    GdprSchedulerService,
    GenealogyService,
    TieredCompensationsService,
    DistributorOrdersService,
    HomeService,
    PaymentsService,
    UtilitiesPanelService,
    PlanService,
    BsModalRef,
    ActivityLogReportService,
    InterfaceMonitoringReportService,
    provideAnimations(),
    provideToastr({
      timeOut: 0,
      extendedTimeOut: 0,
      tapToDismiss: true,
      maxOpened: 1,
      autoDismiss: true
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
